import { StoreModel, UTILS } from '@aksia/infrastructure';

export type TStoreModelClass<U> = new (...args: any[]) => U;

export const DEFAULT_STORE_MODEL_FIELD_REMOVALS: ReadonlySet<string> = new Set([
  '$uid',
  '$isDeleted',
  '$disabled',
  '$onTransform',
  '$toDTO',
]);

export function toStoreModelCollection<T, U = StoreModel>(
  dto: Array<T>,
  model?: TStoreModelClass<U>,
  fieldRemovals: ReadonlySet<string> = DEFAULT_STORE_MODEL_FIELD_REMOVALS,
): Array<U> {
  return dto?.map((item) =>
    toStoreModel<T, U>(item, model, fieldRemovals),
  ) as Array<U>;
}

export function toStoreModel<T, U = StoreModel>(
  dto: T,
  model?: TStoreModelClass<U>,
  fieldRemovals: ReadonlySet<string> = DEFAULT_STORE_MODEL_FIELD_REMOVALS,
): U {
  let modelInstance = () =>
    model
      ? (new model(dto) as T extends StoreModel ? T : StoreModel)
      : new StoreModel();

  if (!dto || UTILS.OBJECT.isObject(dto) === false) {
    return modelInstance() as U;
  }

  let sanitizedDto = Object.fromEntries(
    Object.entries(dto).filter(
      ([key, value]) => !fieldRemovals.has(key) && value !== null,
    ),
  );
  let entity = Object.assign(modelInstance(), sanitizedDto);
  if (entity.$onTransform) {
    entity.$onTransform();
  }

  for (const key in entity) {
    if (fieldRemovals.has(key)) continue;
    let field = key as keyof typeof entity;
    let modelField = key as keyof typeof modelInstance;
    let fieldType =
      typeof entity[key] === 'string'
        ? StoreModel.getJsonType(
            field as string,
            model ? new model()?.constructor.name : 'StoreModel',
          )
        : StoreModel.getType(
            field as string,
            model ? new model()?.constructor.name : 'StoreModel',
          );

    if (
      Array.isArray(entity[field]) &&
      entity[field].every((item) => UTILS.OBJECT.isObject(item))
    ) {
      (entity[field] as Object) = toStoreModelCollection(
        entity[field],
        fieldType as TStoreModelClass<StoreModel>,
        fieldRemovals,
      );
    } else if (
      UTILS.OBJECT.isObject(entity[field]) ||
      (entity[field] && fieldType)
    ) {
      let fieldClass = modelInstance()[modelField];

      if (typeof entity[field] === 'string') {
        entity[field] = JSON.parse(entity[field] as string);
      }

      entity[field] = toStoreModel(
        entity[field],
        fieldType
          ? (fieldType as TStoreModelClass<StoreModel>)
          : Object(fieldClass).constructor,
        fieldRemovals,
      );
    }
  }

  return entity as U;
}

export function toStoreModelDTO<T extends StoreModel>(
  entity: T | Array<T>,
  fieldRemovals: ReadonlySet<string> = DEFAULT_STORE_MODEL_FIELD_REMOVALS,
): T | Array<T> {
  return JSON.parse(
    JSON.stringify(entity, (key: string, value: unknown) =>
      fieldRemovals.has(key) ? undefined : value,
    ),
  );
}
